import React, { useContext, useRef, MutableRefObject, useEffect } from "react"
import clsx from "clsx"
import Link from "next/link"

import { DefaultLayoutForReader } from "src/components/common/layouts/DefaultLayoutForReader"
import HTMLHead from "src/components/utils/HTMLHead"
import HTMLHeadForSSR from "src/components/utils/HTMLHeadForSSR"
import routes from "routes"
import URLUtils from "src/utils/URLUtils"
import { ApiUserData } from "src/models/user/ApiUserData"
import { globalStoreContext } from "src/store/GlobalStore"
import { ProfileCard } from "src/components/userPage/ProfileCard"
import styles from "./layout.module.scss"

type ContentName = "Works" | "Request" | "Gift"

type Props = {
  user: ApiUserData
  children?: JSX.Element
  currentContent?: ContentName
}

export const UserPageLayout: React.FC<Props> = ({
  user,
  children,
  currentContent,
}) => {
  const {
    globalStore: {
      user: { apiUserData, clientState },
    },
  } = useContext(globalStoreContext)

  const isCurrent = (target: ContentName) => {
    return target === currentContent
  }

  const tabsElm = useRef<HTMLUListElement | null>(null)
  const worksRef = useRef<HTMLLIElement | null>(null)
  const giftRef = useRef<HTMLLIElement | null>(null)
  const requestRef = useRef<HTMLLIElement | null>(null)

  const targetRefMap = new Map<
    ContentName,
    MutableRefObject<HTMLLIElement | null>
  >([
    ["Works", worksRef],
    ["Request", giftRef],
    ["Gift", requestRef],
  ])

  const targetRef = currentContent
    ? targetRefMap.get(currentContent)
    : undefined

  useEffect(() => {
    // 最初のタブがカレントのときは何もしない
    if (!targetRef || currentContent === "Works") return

    if (tabsElm?.current && targetRef.current) {
      tabsElm.current.scroll(targetRef.current.offsetLeft, 0)
    }
  }, [children, currentContent, tabsElm, targetRef])

  const title = () => {
    switch (currentContent) {
      case "Works":
        return `${user.display_name} | 参加作品一覧`
      case "Request":
        return `${user.display_name} | ほしいものを依頼する`
      case "Gift":
        return `${user.display_name} | ギフトを贈る`
      default:
        return user.display_name
    }
  }

  return (
    <DefaultLayoutForReader>
      <HTMLHead path="user" title={title()} />
      {/* SSR用のメタタグ */}
      <HTMLHeadForSSR
        description={user.profile ?? ""}
        image={`${process.env.NEXT_PUBLIC_S3_URL}${
          user.icon_url ?? "img/user-icon.png"
        }`}
        title={`${user.name} | たいあっぷ`}
        type="article"
        twitterCard="summary"
        url={`${URLUtils.getRootURLFromEnv()}${routes.userTop(
          user.display_name,
        )}`}
      />
      <article className={styles.layout}>
        <ProfileCard
          user={user}
          apiUserData={apiUserData}
          clientState={clientState}
        />
        <div className={styles.tabs}>
          <ul className={styles.tabs_inner} ref={tabsElm}>
            <li
              className={clsx({
                [styles.tab]: true,
                [styles.isActive]: isCurrent("Works"),
              })}
              ref={worksRef}
            >
              <Link href={`/user/${user.display_name}`}>
                <a>投稿作品</a>
              </Link>
            </li>
            <li
              className={clsx({
                [styles.tab]: true,
                [styles.isActive]: isCurrent("Gift"),
              })}
              ref={giftRef}
            >
              <Link href={`/user/${user.display_name}/gift`}>
                <a>ギフトを贈る</a>
              </Link>
            </li>
            <li
              className={clsx({
                [styles.tab]: true,
                [styles.isActive]: isCurrent("Request"),
              })}
              ref={requestRef}
            >
              <Link href={`/user/${user.display_name}/request`}>
                <a className={styles.request}>
                  ほしいものを依頼する<span className={styles.beta}>BETA</span>
                </a>
              </Link>
            </li>
          </ul>
        </div>
        <div className={styles.contents}>{children}</div>
      </article>
    </DefaultLayoutForReader>
  )
}
